import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, RotateCcw, History } from "lucide-react";
import Odontogram from "@/components/dental/Odontogram";
import LabAttachmentsPanel from "@/components/LabAttachmentsPanel";
import TreatmentPlanPanel from "@/components/dental/TreatmentPlanPanel";
import RecallPanel from "@/components/dental/RecallPanel";
import { styleFor } from "@/lib/dental";

export default function DentalPatientChart() {
  const { id } = useParams();
  const [patient, setPatient] = useState(null);
  const [chart, setChart] = useState(null);
  const [meta, setMeta] = useState(null);
  const [history, setHistory] = useState([]);
  const [xrays, setXrays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [p, c, m, h, x] = await Promise.all([
        api.get(`/patients/${id}`),
        api.get(`/dental/patients/${id}/chart`),
        api.get("/dental/meta"),
        api.get(`/dental/patients/${id}/findings?limit=100`),
        api.get(`/patients/${id}/lab-results`).catch(() => ({ data: [] })),
      ]);
      setPatient(p.data);
      setChart(c.data);
      setMeta(m.data);
      setHistory(h.data || []);
      setXrays(x.data || []);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "Could not load chart");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const refreshChart = async () => {
    try {
      const [c, h] = await Promise.all([
        api.get(`/dental/patients/${id}/chart`),
        api.get(`/dental/patients/${id}/findings?limit=100`),
      ]);
      setChart(c.data);
      setHistory(h.data || []);
    } catch { /* non-fatal: the tab the user is on still works */ }
  };

  const saveFindings = async (pending) => {
    setSaving(true);
    try {
      const { data } = await api.post(`/dental/patients/${id}/findings`, { findings: pending });
      setChart(data.chart);
      toast.success(`${data.added} finding${data.added === 1 ? "" : "s"} charted`);
      const h = await api.get(`/dental/patients/${id}/findings?limit=100`);
      setHistory(h.data || []);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "Could not save");
      throw e;   // Odontogram keeps the pending edits so nothing is lost.
    } finally {
      setSaving(false);
    }
  };

  const saveLayout = async (payload) => {
    try {
      const { data } = await api.put(`/dental/patients/${id}/chart/layout`, payload);
      setChart(data);
      toast.success(payload?.reset ? "Chart reset to standard positions" : "Tooth positions saved");
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "Could not save tooth positions");
      throw e;   // Odontogram stays in arrange mode so nothing is lost.
    }
  };

  const voidFinding = async (fid) => {
    const reason = window.prompt("Reason for voiding this finding?");
    if (reason === null) return;
    try {
      const { data } = await api.post(`/dental/findings/${fid}/void`, { reason });
      setChart(data.chart);
      const h = await api.get(`/dental/patients/${id}/findings?limit=100`);
      setHistory(h.data || []);
      toast.success("Finding voided");
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    }
  };

  if (loading) return <div className="p-8 text-slate-500">Loading chart…</div>;
  if (!patient) return <div className="p-8 text-slate-500">Patient not found.</div>;

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="dental-patient-chart">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Button asChild variant="ghost" size="sm">
            <Link to="/dental"><ArrowLeft size={14} className="mr-1" /> Dental</Link>
          </Button>
          <div>
            <h1 className="text-xl font-extrabold text-slate-900" style={{ fontFamily: "Work Sans" }}>
              {patient.full_name}
            </h1>
            <div className="text-xs text-slate-500">
              {patient.gender || "—"} · {patient.birthday || "no birthday"} · {patient.contact_number || "no contact"}
            </div>
          </div>
        </div>
        <Button asChild variant="outline" size="sm">
          <Link to={`/patients/${id}`}>Full patient record</Link>
        </Button>
      </div>

      <Tabs defaultValue="chart">
        <TabsList>
          <TabsTrigger value="chart" data-testid="tab-odontogram">Odontogram</TabsTrigger>
          <TabsTrigger value="plan" data-testid="tab-plan">Treatment Plan</TabsTrigger>
          <TabsTrigger value="recalls" data-testid="tab-recalls">Recalls</TabsTrigger>
          <TabsTrigger value="xrays" data-testid="tab-xrays">X-rays ({xrays.length})</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">History ({history.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="chart" className="mt-4">
          <Odontogram chart={chart} meta={meta} onSave={saveFindings} onSaveLayout={saveLayout} saving={saving} />
        </TabsContent>

        <TabsContent value="plan" className="mt-4">
          {/* Completing a plan item writes a finding server-side, so the
              odontogram held here would otherwise show stale state until a
              reload — refresh it when that happens. */}
          <TreatmentPlanPanel patientId={id} meta={meta} onCharted={refreshChart} />
        </TabsContent>

        <TabsContent value="recalls" className="mt-4">
          <RecallPanel patientId={id} />
        </TabsContent>

        <TabsContent value="xrays" className="mt-4">
          <LabAttachmentsPanel
            patientId={id}
            attachments={xrays}
            onChanged={async () => {
              const x = await api.get(`/patients/${id}/lab-results`);
              setXrays(x.data || []);
            }}
          />
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <Card className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <History size={16} className="text-slate-500" />
              <h3 className="font-semibold text-slate-900">Charting history</h3>
            </div>
            {history.length === 0 ? (
              <div className="text-sm text-slate-500">Nothing charted yet.</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {history.map((f) => {
                  const s = styleFor(f.finding_type);
                  return (
                    <div key={f.id} className="py-2 flex items-center gap-3" data-testid={`finding-${f.id}`}>
                      <span
                        className="w-6 h-6 rounded flex items-center justify-center text-[10px] font-bold shrink-0"
                        style={{ background: s.fill, color: s.text, border: `1px solid ${s.stroke}` }}
                      >
                        {s.glyph}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-slate-900">
                          <b>#{f.tooth}</b> {s.label}
                          {f.surfaces?.length ? ` (${f.surfaces.join("")})` : ""}
                          {f.material ? ` · ${f.material}` : ""}
                          {f.status !== "existing" ? ` · ${f.status}` : ""}
                        </div>
                        <div className="text-[11px] text-slate-400">
                          {(f.recorded_at || "").slice(0, 16).replace("T", " ")} · {f.recorded_by_name || "—"}
                          {f.note ? ` · ${f.note}` : ""}
                        </div>
                      </div>
                      <Button
                        size="sm" variant="ghost"
                        className="text-red-600 hover:bg-red-50 shrink-0"
                        onClick={() => voidFinding(f.id)}
                        data-testid={`btn-void-${f.id}`}
                      >
                        <RotateCcw size={13} className="mr-1" /> Void
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="mt-3 text-[11px] text-slate-400">
              Findings are never deleted — voiding keeps the correction in the record.
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
